import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { domainsAPI, wordpressAPI } from '@hpanel/web-common/api/client'
import type { Domain, WordPressInstall } from '@hpanel/web-common/api/types'

/** One-time credentials from install / reset. Lives only in component state. */
interface Credentials {
  installId: number
  domain: string
  username: string
  password: string
}

/**
 * Tenant WordPress page (Plan 6 + F6).
 *
 * Lists installs, install on a domain, and per-install actions:
 * update / clone-to-staging / reset admin password / delete.
 *
 * The generated WordPress admin password is shown ONCE (install or reset)
 * and is never stored by the panel.
 */
export const WordPressManager: React.FC = () => {
  const [installs, setInstalls] = useState<WordPressInstall[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const location = useLocation()
  const notice = (location.state as { notice?: string } | null)?.notice ?? null

  // Install form
  const [showForm, setShowForm] = useState(false)
  const [formDomainId, setFormDomainId] = useState<number | ''>('')
  const [formEmail, setFormEmail] = useState('')

  // One-time credentials box + reset confirmation (single-action modal)
  const [credentials, setCredentials] = useState<Credentials | null>(null)
  const [copied, setCopied] = useState(false)
  const [resetTarget, setResetTarget] = useState<WordPressInstall | null>(null)
  const [resetting, setResetting] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const [is, ds] = await Promise.all([
        wordpressAPI.listMine(),
        domainsAPI.list(),
      ])
      setInstalls(is)
      setDomains(ds.domains)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  const install = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formDomainId === '' || !formEmail) return
    try {
      const result = await wordpressAPI.install({
        domain_id: Number(formDomainId),
        admin_email: formEmail,
      })
      setShowForm(false)
      setError(null)
      setInfo(`Installed at ${result.document_root}.`)
      setCopied(false)
      setCredentials({
        installId: result.id,
        domain: domainName(result.domain_id),
        username: result.admin_username ?? '',
        password: result.admin_password,
      })
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Install failed')
    }
  }

  const copyPassword = async () => {
    if (!credentials) return
    try {
      await navigator.clipboard.writeText(credentials.password)
      setCopied(true)
    } catch (e: unknown) {
      console.error('Copy to clipboard failed', e)
      setError('Could not copy automatically — select the password and copy it manually.')
    }
  }

  const confirmReset = async () => {
    if (!resetTarget) return
    const target = resetTarget
    setResetting(true)
    setError(null)
    setInfo(null)
    try {
      const result = await wordpressAPI.resetAdminPassword(target.id)
      setResetTarget(null)
      setCopied(false)
      setCredentials({
        installId: result.id,
        domain: domainName(result.domain_id),
        username: result.admin_username ?? '',
        password: result.admin_password,
      })
    } catch (e: unknown) {
      console.error('WordPress admin password reset failed', e)
      setResetTarget(null)
      setError(e instanceof Error ? e.message : 'Password reset failed')
    } finally {
      setResetting(false)
    }
  }

  // The update call is synchronous: the server snapshots the site, then runs
  // the core, plugin and theme updates and only responds when they are done.
  const update = async (id: number) => {
    setBusyId(id)
    setInfo(null)
    setError(null)
    try {
      await wordpressAPI.update(id, { core: true, plugins: true, themes: true })
      setInfo(
        `WordPress, plugins and themes on ${domainName(
          installs.find((i) => i.id === id)?.domain_id ?? -1,
        )} are up to date. A snapshot was taken before updating.`,
      )
      await refresh()
    } catch (e: unknown) {
      console.error('WordPress update failed', e)
      setError(e instanceof Error ? e.message : 'Update failed')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (id: number) => {
    if (
      !window.confirm(
        "Delete this WordPress install record?\n\nThe panel removes the row but does NOT delete the on-disk files — wipe them via the File Manager if you want a fully-clean state.",
      )
    ) {
      return
    }
    try {
      await wordpressAPI.delete(id)
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const domainName = (id: number) =>
    domains.find((d) => d.id === id)?.name ?? `domain #${id}`

  return (
    <div data-testid="wordpress-manager-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="page-title" className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            WordPress
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            One-click installs, automatic pre-update snapshots, and staging
            copies to try changes on.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="wordpress-toggle-install-button"
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
        >
          {showForm ? 'Cancel' : '+ Install WordPress'}
        </button>
      </div>

      {error && (
        <div
          data-testid="wp-error"
          className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3 text-sm"
        >
          {error}
        </div>
      )}
      {notice && !info && (
        <div
          data-testid="wp-notice"
          className="bg-green-50 dark:bg-green-900/20 border border-green-300 text-green-800 dark:text-green-200 rounded p-3 text-sm"
        >
          {notice}
        </div>
      )}
      {info && (
        <div
          data-testid="wp-info"
          className="bg-green-50 dark:bg-green-900/20 border border-green-300 text-green-800 dark:text-green-200 rounded p-3 text-sm"
        >
          {info}
        </div>
      )}

      {credentials && (
        <div
          data-testid="wp-credentials"
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-400 rounded-lg p-4 space-y-2 text-sm"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            WordPress admin login for {credentials.domain}
          </h2>
          <p className="text-amber-900 dark:text-amber-200">
            Save this password now. It will not be shown again — if you lose it,
            use “Reset admin password”.
          </p>
          <div>
            Username:{' '}
            <code data-testid="wp-credentials-username" className="font-mono">
              {credentials.username}
            </code>
          </div>
          <div>
            Password:{' '}
            <code data-testid="wp-credentials-password" className="font-mono break-all">
              {credentials.password}
            </code>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="wp-credentials-copy"
              onClick={copyPassword}
              className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded"
            >
              {copied ? 'Copied' : 'Copy password'}
            </button>
            <a
              data-testid="wp-credentials-admin-link"
              href={`https://${credentials.domain}/wp-admin`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline"
            >
              Open wp-admin
            </a>
            <button
              type="button"
              data-testid="wp-credentials-dismiss"
              onClick={() => setCredentials(null)}
              className="ml-auto text-xs text-gray-600 dark:text-gray-400 underline"
            >
              I have saved it
            </button>
          </div>
        </div>
      )}

      {resetTarget && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="wp-reset-confirm"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Reset admin password?
            </h2>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              A new password is set for {resetTarget.admin_username ?? 'the admin user'} on{' '}
              {domainName(resetTarget.domain_id)}. The old password stops working
              immediately. The new one is shown once.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="wp-reset-cancel"
                disabled={resetting}
                onClick={() => setResetTarget(null)}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="wp-reset-confirm-button"
                disabled={resetting}
                onClick={confirmReset}
                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-sm"
              >
                {resetting ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form
          onSubmit={install}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Install on a domain
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Domain
              </label>
              <select
                value={formDomainId}
                onChange={(e) =>
                  setFormDomainId(
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
                required
                className="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              >
                <option value="">— select —</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Admin email
              </label>
              <input
                type="email"
                required
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              />
            </div>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
          >
            Install
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      ) : installs.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">
          No WordPress installs yet.
        </p>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-left">
              <tr>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last update</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {installs.map((i) => (
                <tr
                  key={i.id}
                  className="border-t border-gray-200 dark:border-gray-700"
                >
                  <td className="px-4 py-3">
                    {domainName(i.domain_id)}
                    {domains.some((d) => d.id === i.domain_id) && (
                      <a
                        href={`https://${domainName(i.domain_id)}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${domainName(i.domain_id)} in a new tab`}
                        title="Open site in a new tab"
                        data-testid={`wp-open-site-${i.id}`}
                        className="ml-2 inline-flex align-middle text-gray-500 hover:text-primary-600"
                      >
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
                      </a>
                    )}
                    {i.staging_of != null && (
                      <span
                        data-testid={`wp-staging-badge-${i.id}`}
                        className="ml-2 inline-block px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 text-xs rounded"
                      >
                        Staging copy of{' '}
                        {i.staging_of_domain ?? `site #${i.staging_of}`}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono">
                    {i.wp_version ?? '—'}
                  </td>
                  <td className="px-4 py-3">{i.status}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {i.last_update_at?.replace('T', ' ').slice(0, 16) ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      disabled={busyId === i.id}
                      data-testid={`wp-update-${i.id}`}
                      onClick={() => update(i.id)}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded"
                    >
                      {busyId === i.id ? 'Updating…' : 'Update'}
                    </button>
                    {i.staging_of == null && (
                      <Link
                        to={`/client/wordpress/${i.id}/staging`}
                        data-testid={`wp-clone-${i.id}`}
                        className="inline-block px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                      >
                        Clone to staging
                      </Link>
                    )}
                    <button
                      disabled={busyId === i.id || i.status !== 'ready'}
                      data-testid={`wp-reset-password-${i.id}`}
                      onClick={() => setResetTarget(i)}
                      className="px-2 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs rounded"
                    >
                      Reset admin password
                    </button>
                    <button
                      onClick={() => remove(i.id)}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
